import { useState, useEffect, useCallback } from 'react';
import { authenticatedFetch } from '@/lib/api';

export interface IntegrityMismatch {
  terminal_id: string;
  nickname: string;
  ssid: string;
  unit_id: string;
  unit_code: string;
  external_terminal_id: string;
  service_line_number: string;
  connection_status: string;
  last_seen_at: string | null;
}

export interface IntegrityUnlinked {
  terminal_id: string;
  nickname: string;
  ssid: string;
  external_terminal_id: string;
  service_line_number: string;
  connection_status: string;
  last_seen_at: string | null;
  is_active: boolean;
}

export interface IntegrityUncovered {
  unit_id: string;
  unit_code: string;
}

export interface SolarkPlantIssue {
  plant_id: string;
  external_plant_id: string;
  plant_name: string | null;
  unit_id: string;
  unit_code: string;
}

export interface SolarkUnlinkedPlant {
  plant_id: string;
  external_plant_id: string;
  plant_name: string | null;
}

export interface SolarkInverterIssue {
  inverter_id: string;
  external_inverter_id: string;
  last_seen_at: string | null;
  unit_id?: string;
  unit_code?: string;
}

export interface SolarkLinkConflict extends SolarkPlantIssue {
  inverter_id: string;
  inverter_unit_id: string;
  inverter_unit_code: string | null;
}

export interface SolarkIntegrity {
  /** Linked plant with no solark_inverters row — the trailer gets no telemetry (ISS-046). */
  plants_without_inverter: SolarkPlantIssue[];
  /** Plant linked to one unit, its inverter row to another — telemetry lands on the wrong trailer. */
  link_conflicts?: SolarkLinkConflict[];
  mismatches: SolarkPlantIssue[];
  unlinked_plants: SolarkUnlinkedPlant[];
  unlinked_inverters: SolarkInverterIssue[];
  uncovered: IntegrityUncovered[];
  never_reported: SolarkInverterIssue[];
}

export interface TelemetryCoverage {
  last_run_at: string;
  status: string;
  /** Rows the run attempted. */
  items_processed: number;
  /** Rows the run successfully updated — what `short_by` is measured against. */
  items_success: number;
  linked_inverters: number;
  short_by: number;
}

export interface IntegritySummary {
  starlink: number;
  solark: number;
  discovery: number;
  telemetry: number;
  total: number;
}

export interface IntegrityIssues {
  mismatches: IntegrityMismatch[];
  unlinked: IntegrityUnlinked[];
  uncovered: IntegrityUncovered[];
  /** Absent on an older API; the panel treats missing as none. */
  solark?: SolarkIntegrity;
  discovery?: { pending: number | null };
  telemetry?: TelemetryCoverage | null;
  summary?: IntegritySummary;
}

export function useDeviceIntegrity() {
  const [issues, setIssues] = useState<IntegrityIssues | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch_ = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await authenticatedFetch('/api/v1/admin/devices/integrity');
      if (!res.ok) {
        if (res.status === 403) {
          setIssues(null);
          return;
        }
        throw new Error(`HTTP ${res.status}`);
      }
      setIssues(await res.json());
      setError(null);
    } catch (e: unknown) {
      setError((e as Error)?.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetch_(); }, [fetch_]);

  // The server's summary counts every actionable gap; fall back to the
  // Starlink-only count for an older API.
  const totalIssues = issues
    ? (issues.summary?.total ?? issues.mismatches.length + issues.unlinked.length)
    : 0;

  return { issues, isLoading, error, totalIssues, refresh: fetch_ };
}
